import React, { useEffect, useRef } from 'react';
import Icon from './Icon';
import { IconName } from './iconSprite';

/* ── Дизайн системийн суурь компонентууд (handoff spec) ── */

type BtnProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'; size?: 'md' | 'sm' | 'cta'; block?: boolean;
  icon?: IconName; loading?: boolean;
};
export function Button({ variant = 'primary', size = 'md', block, icon, loading, children, className = '', disabled, ...rest }: BtnProps) {
  const cls = ['sh-btn', `sh-btn--${variant}`, size === 'sm' ? 'sh-btn--sm' : '', size === 'cta' ? 'sh-btn--cta' : '', block ? 'sh-btn--block' : '', className].filter(Boolean).join(' ');
  return (
    <button className={cls} disabled={disabled || loading} aria-busy={loading || undefined} {...rest}>
      {loading ? <span className="sh-spinner" aria-hidden /> : icon ? <Icon name={icon} size={size === 'sm' ? 16 : 18} /> : null}
      {children}
    </button>
  );
}

export function IconButton({ icon, label, size = 44, className = '', ...rest }: React.ButtonHTMLAttributes<HTMLButtonElement> & { icon: IconName; label: string; size?: 40 | 44 }) {
  return <button type="button" className={`sh-iconbtn ${size === 40 ? 'sh-iconbtn--sm' : ''} ${className}`} aria-label={label} title={label} {...rest}><Icon name={icon} size={20} /></button>;
}

type FieldProps = { label?: string; optional?: boolean; error?: string; help?: string; children: React.ReactNode; id?: string };
export function Field({ label, optional, error, help, children, id }: FieldProps) {
  return (
    <div className="sh-field">
      {label && <label className="sh-label" htmlFor={id}>{label}{optional && <span className="opt"> (заавал биш)</span>}</label>}
      {children}
      {error ? <div className="sh-error" role="alert"><Icon name="alert" size={14} />{error}</div> : help ? <div className="sh-help">{help}</div> : null}
    </div>
  );
}
export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }>(({ invalid, className = '', ...p }, ref) =>
  <input ref={ref} className={`sh-input ${className}`} aria-invalid={invalid || undefined} {...p} />);
export const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }>(({ invalid, className = '', ...p }, ref) =>
  <textarea ref={ref} className={`sh-textarea ${className}`} aria-invalid={invalid || undefined} {...p} />);
export function Select({ className = '', children, ...p }: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={`sh-select ${className}`} {...p}>{children}</select>;
}

export function Chip({ tone = 'new', icon, on, red, children, onClick, className = '' }: { tone?: 'paid' | 'debt' | 'progress' | 'new' | 'filter'; icon?: IconName; on?: boolean; red?: boolean; children: React.ReactNode; onClick?: () => void; className?: string }) {
  const cls = `sh-chip sh-chip--${tone} ${on ? 'is-on' : ''} ${red ? 'is-red' : ''} ${className}`;
  return onClick
    ? <button type="button" className={cls} onClick={onClick} aria-pressed={on}>{icon && <Icon name={icon} size={14} />}{children}</button>
    : <span className={cls}>{icon && <Icon name={icon} size={14} />}{children}</span>;
}

export function Card({ children, className = '', style }: { children: React.ReactNode; className?: string; style?: React.CSSProperties }) {
  return <div className={`sh-card ${className}`} style={style}>{children}</div>;
}

function useEscape(onClose: () => void) {
  useEffect(() => { const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); }; document.addEventListener('keydown', h); return () => document.removeEventListener('keydown', h); }, [onClose]);
}
export function Modal({ title, onClose, width = 420, children, footer }: { title: string; onClose: () => void; width?: 380 | 420 | 440 | 680; children: React.ReactNode; footer?: React.ReactNode }) {
  useEscape(onClose);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { ref.current?.querySelector<HTMLElement>('button,input,select,textarea,[tabindex]')?.focus(); }, []);
  return (
    <div className="sh-overlay" onClick={onClose}>
      <div ref={ref} className="sh-modal" style={{ ['--w' as any]: `${width}px` }} role="dialog" aria-modal="true" aria-label={title} onClick={e => e.stopPropagation()}>
        <div className="sh-modal__head"><h3>{title}</h3><IconButton icon="x" label="Хаах" size={40} onClick={onClose} style={{ background: 'var(--bg)' }} /></div>
        {children}
        {footer && <div className="sh-modal__foot">{footer}</div>}
      </div>
    </div>
  );
}
export function SlideOver({ title, onClose, children, footer }: { title: string; onClose: () => void; children: React.ReactNode; footer?: React.ReactNode }) {
  useEscape(onClose);
  return (
    <div className="sh-overlay sh-overlay--right" onClick={onClose}>
      <div className="sh-slideover" role="dialog" aria-modal="true" aria-label={title} onClick={e => e.stopPropagation()}>
        <div className="sh-slideover__head"><h3>{title}</h3><IconButton icon="x" label="Хаах" size={40} onClick={onClose} style={{ background: 'var(--bg)' }} /></div>
        <div className="sh-slideover__body">{children}</div>
        {footer && <div className="sh-slideover__foot">{footer}</div>}
      </div>
    </div>
  );
}

export function Toast({ message, error, action, onAction, onClose, ms = 4000 }: { message: string; error?: boolean; action?: string; onAction?: () => void; onClose: () => void; ms?: number }) {
  useEffect(() => { if (error) return; const t = setTimeout(onClose, ms); return () => clearTimeout(t); }, [error, ms, onClose]);
  return (
    <div className={`sh-toast ${error ? 'sh-toast--error' : ''}`} role="status">
      <Icon name={error ? 'alert' : 'check-circle'} size={20} className={error ? '' : 'ok'} />
      <span>{message}</span>
      {action && <button className="sh-toast__action" onClick={onAction}>{action}</button>}
      {error && <button className="sh-toast__action" onClick={onClose} aria-label="Хаах">✕</button>}
    </div>
  );
}

export function Empty({ icon = 'search', title, text }: { icon?: IconName; title: string; text?: string }) {
  return <div className="sh-empty"><div className="sh-empty__icon"><Icon name={icon} size={26} /></div><h4>{title}</h4>{text && <div style={{ fontSize: 13 }}>{text}</div>}</div>;
}
export function Skeleton({ w = '100%', h = 14, style }: { w?: number | string; h?: number; style?: React.CSSProperties }) {
  return <div className="sh-skeleton" style={{ width: w, height: h, ...style }} />;
}
export function Note({ tone, icon = 'info', children }: { tone?: 'warn' | 'danger'; icon?: IconName; children: React.ReactNode }) {
  return <div className={`sh-note ${tone ? `sh-note--${tone}` : ''}`}><Icon name={icon} size={18} style={{ marginTop: 1 }} /><div>{children}</div></div>;
}
export function Logo({ size = 40 }: { size?: 32 | 36 | 40 }) {
  return <span className="sh-logo" style={{ width: size, height: size }}><Icon name="home" size={Math.round(size * .55)} /></span>;
}
/** Мөнгө: `186 500 ₮` — мянгат зайгаар, ₮ ард (handoff). */
export const money = (n: number) => String(Math.round(n || 0)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' ₮';
export { Icon };
